import { Button } from "@/components/design-system/button";
import { classApi } from "../api/classApi";
import ClassForm from "../components/ClassForm";
import type { ClassFormSchemaValues } from "../schemas/classForm.schema";
import {
  showApiErrorToast,
  showApiSuccessToast,
} from "../utils/apiToastMessage";
import { ChevronLeft } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useLinkableGrades } from "../hooks/useLinkableGrades";
import { ADMIN_CLASS_USE_MOCK } from "../config/admin.config";
import type { AdminClass } from "../types/class.types";

export default function EditClassPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [initial, setInitial] = useState<AdminClass | null>(null);
  const { grades, loading: gradesLoading } = useLinkableGrades(ADMIN_CLASS_USE_MOCK);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) {
      setLoading(false);
      setError("Class not found.");
      return;
    }

    classApi
      .getClassById(id)
      .then((record) => {
        if (!record) {
          setError("Class not found.");
          return;
        }
        setInitial(record);
      })
      .catch(() => setError("Could not load class."))
      .finally(() => setLoading(false));
  }, [id]);

  const handleSubmit = async (values: ClassFormSchemaValues) => {
    if (!id) return;
    try {
      const result = await classApi.updateClass(id, values);
      showApiSuccessToast(result.message, "Class updated successfully");
      navigate("/class-management");
    } catch (e) {
      console.error(e);
      showApiErrorToast(e, "Could not update class. Please try again.");
    }
  };

  return (
    <div className="h-full flex flex-col">
      <div className="h-[68px] px-[32px] items-center flex gap-3">
        <Button
          type="button"
          variant="light"
          size="md"
          className="w-[40px] h-[40px] p-0 bg-white border border-[#EDEDED] rounded-[8px]"
          onClick={() => navigate(-1)}
          leftIcon={<ChevronLeft className="w-5 h-5" />}
        >
          <span className="sr-only">Back</span>
        </Button>
        <div className="text-[#1F1F1F] text-[16px] font-semibold">Edit Class</div>
      </div>
      <div className="flex-1 px-[32px] pb-[24px]">
        {loading && (
          <p className="text-[16px] font-medium py-8">Loading class…</p>
        )}
        {error && <p className="text-[#ef4444] py-8">{error}</p>}
        {!loading && !error && initial && (
          <ClassForm
            mode="edit"
            initial={initial}
            grades={grades}
            gradesLoading={gradesLoading}
            onSubmit={handleSubmit}
            onClose={() => navigate(-1)}
            submitLabel="Update"
          />
        )}
      </div>
    </div>
  );
}
